'use client';

import { forwardRef, ReactNode, useState } from 'react';
import * as RadixDropdownMenu from '@radix-ui/react-dropdown-menu';
import { ChevronDown } from 'lucide-react';
import { cn } from '@ecom/helper-react-class-combine';

type DropdownItem = {
  label: string | ReactNode;
  value: string;
  onClick?: () => void;
  isActive?: boolean;
  activeClassName?: string;
};

type DropdownProps = {
  items: DropdownItem[];
  ariaLabel?: string;
  align?: 'start' | 'center' | 'end';
  modal?: boolean;
  className?: string;
  triggerClassName?: string;
  contentClassName?: string;
  itemClassName?: string;
  activeItemClassName?: string;
  menuTitle?: string | ReactNode;
  menuIcon?: React.ReactNode;
  menuIconPosition?: 'start' | 'end';
  withArrow?: boolean;
  variant?: 'text' | 'button';
};

export const DropdownMenu = ({
  items,
  ariaLabel,
  align = 'end',
  modal = false,
  className,
  triggerClassName,
  contentClassName,
  itemClassName,
  activeItemClassName,
  menuTitle,
  menuIcon,
  menuIconPosition = 'end',
  withArrow = false,
  variant = 'text',
}: DropdownProps) => {
  const [open, setOpen] = useState(false);

  return (
    <RadixDropdownMenu.Root open={open} onOpenChange={setOpen} modal={modal}>
      <RadixDropdownMenu.Trigger asChild>
        <button
          type='button'
          className={cn(
            'group inline-flex items-center justify-center gap-2 select-none',
            'box-border focus:ring-0 focus:outline-hidden',
            variant === 'text' && 'mx-2 bg-transparent p-0',
            variant === 'button' && [
              'border px-4 py-2',
              'font-semibold tracking-wide',
              'bg-transparent',
              'border-border',
              'text-text',
              'hover:border-border-hover hover:bg-background-contrast',
            ],
            variant === 'text' && [
              open && 'text-primary',
              !!menuTitle && 'border-b-2',
              !!menuTitle && (open ? 'border-primary' : 'border-transparent'),
            ],
            variant === 'button' && [
              'data-[state=open]:text-text-muted data-[state=open]:opacity-70',
            ],
            triggerClassName,
            className,
          )}
          style={{ outline: 'none' }}
          aria-label={ariaLabel}
          aria-expanded={open}
          aria-haspopup='true'
          data-state={open ? 'open' : 'closed'}
          onMouseDown={(e) => {
            e.preventDefault();
          }}
          onFocus={(e) => {
            e.currentTarget.blur();
          }}
        >
          {menuIconPosition === 'start' && menuIcon}
          <span className={cn('font-semibold')}>{menuTitle}</span>
          {menuIconPosition === 'end' && menuIcon}
          {withArrow && (
            <ChevronDown className='h-4 w-4 transition-transform duration-200 ease-out group-data-[state=open]:rotate-180' />
          )}
        </button>
      </RadixDropdownMenu.Trigger>

      <RadixDropdownMenu.Portal>
        <RadixDropdownMenu.Content
          className={cn(
            'z-50 min-w-[var(--radix-dropdown-menu-trigger-width)] overflow-hidden px-1 py-2 font-semibold',
            'data-[state=closed]:animate-slide-up data-[state=open]:animate-slide-down',
            'max-lg:data-[state=open]:bg-background-contrast',
            'lg:data-[state=open]:bg-background-panel',
            contentClassName,
          )}
          sideOffset={8}
          align={align}
          avoidCollisions
        >
          {items.map((item, index) => (
            <DropdownMenuItem
              key={`${index}-${item.value}`}
              onClick={() => {
                item?.onClick?.();
                setOpen(false);
              }}
              className={cn(
                'p-2',
                itemClassName,
                item.isActive && activeItemClassName,
                item.isActive && item.activeClassName,
              )}
            >
              {item.label}
            </DropdownMenuItem>
          ))}
        </RadixDropdownMenu.Content>
      </RadixDropdownMenu.Portal>
    </RadixDropdownMenu.Root>
  );
};

const DropdownMenuItem = forwardRef<
  React.ComponentRef<typeof RadixDropdownMenu.Item>,
  React.ComponentPropsWithoutRef<typeof RadixDropdownMenu.Item>
>(({ className, children, ...props }, ref) => (
  <RadixDropdownMenu.Item
    ref={ref}
    className={cn(
      'relative flex min-w-[var(--radix-dropdown-menu-trigger-width)] cursor-pointer items-center rounded-xs px-2 py-1.5 text-sm outline-hidden transition-colors select-none',
      'data-[disabled]:pointer-events-none data-[disabled]:opacity-50',
      'data-[highlighted]:text-accent-foreground data-[highlighted]:bg-background-hover',
      className,
    )}
    {...props}
  >
    {children}
  </RadixDropdownMenu.Item>
));

DropdownMenuItem.displayName = RadixDropdownMenu.Item.displayName;
